/**
 * FilterBar - Composant de filtrage réutilisable
 *
 * Props:
 * - filters: Array de configurations de filtres
 *   [{ key: 'status', label: 'Statut', type: 'select', options: [...] }, ...]
 * - values: Object des valeurs actuelles { status: 'active', search: '' }
 * - onChange: (key, value) => void
 * - onReset: () => void (optionnel)
 * - searchPlaceholder: string (optionnel)
 */

import React from 'react';
import { COMMON_STYLES } from '../../utils/constants';

const FILTER_TYPES = {
  SELECT: 'select',
  TEXT: 'text',
  DATE: 'date',
  DATE_RANGE: 'date_range',
  SEARCH: 'search',
  CHECKBOX: 'checkbox'
};

const FilterBar = ({
  filters = [],
  values = {},
  onChange,
  onReset,
  searchPlaceholder = 'Rechercher...',
  style = {}
}) => {
  const hasActiveFilters = Object.values(values).some(v => v !== '' && v !== null && v !== undefined);

  const renderFilter = (filter) => {
    const value = values[filter.key] ?? '';

    switch (filter.type) {
      case FILTER_TYPES.SELECT:
        return (
          <select
            value={value}
            onChange={(e) => onChange(filter.key, e.target.value)}
            style={styles.select}
          >
            <option value="">{filter.placeholder || `Tous`}</option>
            {filter.options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case FILTER_TYPES.TEXT:
      case FILTER_TYPES.SEARCH:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(filter.key, e.target.value)}
            placeholder={filter.placeholder || searchPlaceholder}
            style={styles.input}
          />
        );

      case FILTER_TYPES.DATE:
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => onChange(filter.key, e.target.value)}
            style={styles.input}
          />
        );

      case FILTER_TYPES.DATE_RANGE:
        return (
          <div style={styles.dateRange}>
            <input
              type="date"
              value={values[`${filter.key}_start`] || ''}
              onChange={(e) => onChange(`${filter.key}_start`, e.target.value)}
              style={styles.input}
            />
            <span style={styles.dateRangeSeparator}>-</span>
            <input
              type="date"
              value={values[`${filter.key}_end`] || ''}
              onChange={(e) => onChange(`${filter.key}_end`, e.target.value)}
              style={styles.input}
            />
          </div>
        );

      case FILTER_TYPES.CHECKBOX:
        return (
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(filter.key, e.target.checked)}
              style={styles.checkbox}
            />
            {filter.label}
          </label>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ ...styles.container, ...style }}>
      <div style={styles.filtersRow}>
        {filters.map(filter => (
          <div key={filter.key} style={styles.filterGroup}>
            {filter.type !== FILTER_TYPES.CHECKBOX && filter.label && (
              <label style={styles.label}>{filter.label}</label>
            )}
            {renderFilter(filter)}
          </div>
        ))}
      </div>

      {onReset && hasActiveFilters && (
        <button
          onClick={onReset}
          style={styles.resetButton}
          title="Réinitialiser les filtres"
        >
          Effacer filtres
        </button>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '16px',
    padding: '16px',
    background: '#f8f9fa',
    borderRadius: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  filtersRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    flex: 1
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '150px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057'
  },
  select: {
    padding: '10px 12px',
    border: '2px solid #e1e5e9',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white',
    minHeight: '42px',
    cursor: 'pointer'
  },
  input: {
    padding: '10px 12px',
    border: '2px solid #e1e5e9',
    borderRadius: '8px',
    fontSize: '14px',
    minHeight: '42px'
  },
  dateRange: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  dateRangeSeparator: {
    color: '#6c757d'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '10px 0'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  resetButton: {
    padding: '10px 16px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    minHeight: '42px'
  }
};

// Export des types pour faciliter l'usage
FilterBar.TYPES = FILTER_TYPES;

export default FilterBar;
